/* eslint-disable import/no-cycle */
import { useState } from 'react';
import { Divider, Token, Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { HelpIcon } from 'components';
import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ArticleItem, ListContainer, QueryItem } from 'data/generated/graphql';
import { ManualItems } from './components/ManualItems/ManualItems';
import { QueryRulesItems } from './components/QueryItems/QueryRulesItems';
import styles from './List.module.scss';
import collectionEditStyles from '../../../../features/collection-edit/CollectionEdit.module.scss';

interface ListProps extends CommonEntityProps {
  data: ListContainer;
}

export const List = ({ data: list, parentHierarchyId, index }: ListProps) => {
  const { generateHierarchyId, modifyEntity } = useDataModelContext();
  const hierarchyId = generateHierarchyId(list, parentHierarchyId, index);

  const articleItems = list.collection.filter((item): item is ArticleItem => item.type === EntityType.Article);
  const lastArticleItemIndex = list.collection.findLastIndex((item) => item.type === EntityType.Article);

  const queryItemIndex = list.collection.findIndex((item) => item.type === EntityType.Query);
  const queryItem = queryItemIndex >= 0 ? (list.collection[queryItemIndex] as QueryItem) : undefined;
  const allessehJsonQuery = queryItem ? JSON.stringify(queryItem.attributes.query) : null;

  const [numItemsText, setNumItemsText] = useState<string>(articleItems.length.toString());

  const handleNumItemsLoaded = (numItemsFromQuery: number) => {
    const totalItems = articleItems.length + numItemsFromQuery;
    if (totalItems > 99) {
      setNumItemsText('99+');
    } else {
      setNumItemsText(totalItems.toString());
    }
  };

  const setJsonQueryStr = (newQueryStr: string) => {
    const hierarchyId = `0-${queryItemIndex}`;
    modifyEntity(hierarchyId, (entity) => {
      const query = entity as QueryItem;
      query.attributes.query = JSON.parse(newQueryStr) as Record<string, unknown>;
    });
  };

  return (
    <>
      <Wrapper
        data-testid="collection-items-section-container"
        className={styles.collectionItemsHeader}
        margin={{ bottom: 'md' }}
        data-hierarchy-id={hierarchyId}
      >
        <div data-testid="collection-items-section-header" className={styles.collectionItemsHeaderLeft}>
          <Typography
            data-testid="collection-items-section-header-title"
            variant="header3"
            margin={{ bottom: 'none', right: 'sm' }}
          >
            Collection Items
          </Typography>
          <Token data-testid="collection-items-section-num-items">{numItemsText}</Token>
        </div>
        <HelpIcon
          data-testid="collection-items-section-help-icon"
          text="A collection is a group of curated content and/or an auto-generated list of content from a query. Collections are used for sharing the same content across multiple pages."
        />
      </Wrapper>
      <div data-testid="collection-items-section-content-container" data-model-hierarchy-id={hierarchyId}>
        <Wrapper
          data-testid="collection-items-section-content"
          padding={{ top: 'md', horizontal: 'md' }}
          className={classnames(styles.collectionItemsWrapper, collectionEditStyles.itemsScrollable)}
        >
          <div data-testid="collection-items-section-background" className={styles.collectionItemsWrapperBackground} />
          <Typography
            data-testid="collection-items-section-content-title"
            weight="bold"
            size="sm"
            margin={{ bottom: 'sm' }}
          >
            Curated List
          </Typography>
          <ManualItems
            articles={articleItems}
            allessehJsonQuery={allessehJsonQuery}
            setJsonQueryStr={setJsonQueryStr}
            listHierarchyId={hierarchyId}
          />
          <Divider data-testid="collection-items-section-divider" margin={{ top: 'md', bottom: 'md' }} />
          <Typography
            data-testid="collection-items-section-query-title"
            weight="bold"
            size="sm"
            margin={{ bottom: 'sm' }}
          >
            Queried List
          </Typography>
          <QueryRulesItems
            allessehJsonQuery={allessehJsonQuery}
            lastArticleItemIndex={lastArticleItemIndex}
            setJsonQueryStr={setJsonQueryStr}
            onNumItemsLoaded={handleNumItemsLoaded}
          />
        </Wrapper>
      </div>
    </>
  );
};
